import { useEffect, useRef, useState } from 'react';
import { autocomplete, placeDetails } from '../api/places';
import type { LatLon, PlaceSuggestion, RoutePoint } from '../domain/types';

type AddressSearchProps = {
  label: string;
  placeholder: string;
  value: RoutePoint | null;
  near?: LatLon | null;
  onPick: (point: RoutePoint) => void;
};

// Debounced address autocomplete backed by cache.maps /places/autocomplete.
// On selection, resolves coordinates via /places/details.
export function AddressSearch({ label, placeholder, value, near, onPick }: AddressSearchProps) {
  const [query, setQuery] = useState(value?.address ?? '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionTokenRef = useRef(crypto.randomUUID());

  useEffect(() => {
    setQuery(value?.address ?? '');
  }, [value]);

  const onChange = (text: string) => {
    setQuery(text);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const results = await autocomplete(text, sessionTokenRef.current, near ?? undefined);
      setSuggestions(filterSuggestions(results));
    }, 300);
  };

  const pick = async (s: PlaceSuggestion) => {
    setOpen(false);
    setLoadingDetails(true);
    const address = [s.mainText, s.secondaryText].filter(Boolean).join(', ');
    setQuery(address);
    try {
      const coords = await placeDetails(s.placeId, sessionTokenRef.current);
      if (coords) {
        onPick({ lat: coords.lat, lon: coords.lon, address: coords.address || address });
        sessionTokenRef.current = crypto.randomUUID();
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const needsSelection = query.trim().length > 0 && query !== value?.address;

  return (
    <div className="field-wrapper">
      <div className="field">
        <span className="field-label">{label}</span>
        <input
          className="field-input"
          value={query}
          placeholder={loadingDetails ? 'Визначаємо адресу…' : placeholder}
          disabled={loadingDetails}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
        />
        {open && suggestions.length > 0 && (
          <ul className="suggestions">
            {suggestions.map((s) => (
              <li
                key={s.placeId}
                role="button"
                tabIndex={0}
                onPointerDown={(event) => {
                  event.preventDefault();
                  void pick(s);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void pick(s);
                }}
              >
                <strong>{s.mainText}</strong>
                {s.secondaryText && <span>{s.secondaryText}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {needsSelection && (
        <span className="field-hint">
          Оберіть адресу зі списку підказок, щоб ми отримали координати.
        </span>
      )}
    </div>
  );
}

const specificPlaceTypes = new Set([
  'route',
  'street_address',
  'premise',
  'subpremise',
  'point_of_interest',
  'establishment',
]);

// Hide broad regions/cities (e.g. "Дніпропетровська область") but keep streets
// and house numbers even when Google returns matches outside the selected city.
function filterSuggestions(suggestions: PlaceSuggestion[]): PlaceSuggestion[] {
  return suggestions.filter((suggestion) => {
    if (suggestion.types.length === 0) return true;
    return suggestion.types.some((type) => specificPlaceTypes.has(type));
  });
}
